import {chain, sortBy, unionBy} from 'lodash';
import memoize from 'lru-memoize';
import React from 'react';

import {ApiInboxes, InboxModel} from '../../../../libs/shared/core/src/models/api/inboxModel';
import {TeammateModel} from '../../../../libs/shared/core/src/models/api/teammateModel';
import {buildTeammateDisplayName} from '../../../../libs/shared/core/src/models/helpers/teammateModelHelpers';
import {fetchApiAsync} from '../../../../libs/shared/core/src/services/http/http';
import {DropdownItemContent} from '../../../../libs/shared/ui/src/components/dropdowns/items/dropdownItemContent';
import {createSelector} from '../../../../srcCommon/helpers/reselectHelpers';
import {doesTeammateMatchQuery, teammateCanAccessInboxes} from '../../../helpers/modelHelpers';
import {AppState} from '../../../store/appState';
import {makeGetInboxesById} from '../../../store/data/inboxes/inboxesSelectors';
import {getTeammatesInCommonTeam, getUser} from '../../../store/data/teammates/teammatesSelectors';
import {DropdownItemTeammateAvatar} from '../../common/dropdowns/dropdownItemTeammateAvatar';
import {DropdownFetcher} from '../../common/dropdowns/source/asyncDropdownSource';
import {FilterRowGenerator} from '../../common/filters/resourceMultiSelectFilterDropdown';

/*
 * Props.
 */

export interface LensFormMultiSelectTeammatesContainerProps {
  selectedInboxIds?: ReadonlyArray<number>;
}

/*
 * Selectors.
 */

export function getSelectedInboxIds(state: AppState, props: LensFormMultiSelectTeammatesContainerProps) {
  return props.selectedInboxIds ?? [];
}

export const getInboxesInState = createSelector(
  [makeGetInboxesById(getSelectedInboxIds)],
  (inboxesInState) => inboxesInState,
);

export const getTeammatesInCommon = createSelector(
  [getTeammatesInCommonTeam, getUser],
  (teammatesInCommon, user) => [user, ...teammatesInCommon],
);

export const buildRowGenerator: FilterRowGenerator<TeammateModel> = (
  teammate,
  dropdownSelectedIds,
  makeOnItemSelect,
) => {
  const isSelected = dropdownSelectedIds.includes(teammate.id);

  return {
    isSelected,
    renderer: () => (
      <>
        <DropdownItemTeammateAvatar teammate={teammate} />
        <DropdownItemContent isChecked={isSelected}>{buildTeammateDisplayName(teammate)}</DropdownItemContent>
      </>
    ),
    onSelect: makeOnItemSelect(teammate.id),
    id: teammate.id,
  };
};

export function buildFetcher(
  choices: ReadonlyArray<TeammateModel>,
  initialSelectedIds: ReadonlyArray<number>,
  companyUrl: string,
  selectedInboxesInState: ReadonlyArray<InboxModel>,
  selectedInboxIds?: ReadonlyArray<number>,
  shouldFilterTeammatesByInboxAccess?: boolean,
): DropdownFetcher<TeammateModel, {selectedInboxes: ReadonlyArray<InboxModel>}> {
  return async (query, _items, cancelToken, paginationData) => {
    let newPaginationData = {selectedInboxes: selectedInboxesInState};
    /** Helper function to fetch inboxes if we don't have all selected inboxes in state */
    const getAllSelectedInboxes = async () => {
      // This is a hack - we store the previously fetched inboxes in the pagination data to pass to future
      // requests. Avoids us needing to hit the API if someone is searching for a teammate by name and we already
      // have all the inboxes
      const previouslyFetchedSelectedInboxes = paginationData?.selectedInboxes ?? [];
      const allSelectedInboxes = unionBy(previouslyFetchedSelectedInboxes, selectedInboxesInState, 'id');
      if (!selectedInboxIds || allSelectedInboxes.length === selectedInboxIds.length) {
        // We don't need to fetch anything, just use the inboxes in state
        return allSelectedInboxes;
      }

      // Fetch all the inboxes and filter the teammates by them. Useful for the case where
      // an admin is creating a view and might not have all the inboxes in state.
      const url = `${companyUrl}/inboxes/?ids=${selectedInboxIds.join(',')}`;

      const inboxes = await fetchApiAsync(ApiInboxes, url, {
        cancelToken,
      });

      return inboxes;
    };

    const getFilteredTeammates = async () => {
      if (!shouldFilterTeammatesByInboxAccess) {
        return choices;
      }

      // Fetch the selected inboxes.
      const selectedInboxes = await getAllSelectedInboxes();
      newPaginationData = {selectedInboxes};

      // Filter the teammates based on their access to the selected inboxes.
      const accessFilteredTeammates =
        selectedInboxes.length > 0
          ? choices.filter((teammate) => teammateCanAccessInboxes(teammate, selectedInboxes))
          : choices;

      return accessFilteredTeammates;
    };

    // Fetch the filtered inboxes.
    const accessFilteredTeammates = await getFilteredTeammates();

    // Filter the teammates by which ones match our query
    const items = filterRowsByQuery(accessFilteredTeammates, query);

    return {
      items:
        initialSelectedIds.length > 0 ? sortBy(items, (row) => !initialSelectedIds.includes(row.id)) : items,
      canLoadMore: false,
      paginationData: newPaginationData,
    };
  };
}

const filterRowsByQuery = memoize(20)((teammates: ReadonlyArray<TeammateModel>, searchQuery: string) => {
  // For empty query, render all choices.
  if (!searchQuery) {
    return teammates;
  }

  // Otherwise, filter by name.
  return chain(teammates)
    .filter((teammate) => doesTeammateMatchQuery(teammate, searchQuery))
    .value();
});
